import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

const GovOrganisations = React.lazy(() => import("./GovOrganisations"));
const GovOrganisationHub = React.lazy(() => import("./GovOrganisationHub"));
const GovProposalPage = React.lazy(() => import("./GovProposalPage"));
const GovWorkPage = React.lazy(() => import("./GovWorkPage"));
const GovNotifications = React.lazy(() => import("./GovNotifications"));
const GovInvites = React.lazy(() => import("./GovInvites"));
const GovJobSpecs = React.lazy(() => import("./GovJobSpecs"));
const GovTreasury = React.lazy(() => import("./GovTreasury"));
const GovOrgSettings = React.lazy(() => import("./GovOrgSettings"));
const GovMarketplace = React.lazy(() => import("./GovMarketplace"));
const GovInvitePublic = React.lazy(() => import("./GovInvitePublic"));
const GovOrgPublic = React.lazy(() => import("./GovOrgPublic"));
const GovJoin = React.lazy(() => import("./GovJoin"));
const GovMyParticipation = React.lazy(() => import("./GovMyParticipation"));
const GovActivityEntryPage = React.lazy(() => import("./GovActivityEntryPage"));
const GovLearn = React.lazy(() => import("./GovLearn"));
const GovLab = React.lazy(() => import("./GovLab"));
const GovGuide = React.lazy(() => import("./GovGuide"));

const governanceModuleConfig: ModuleConfig = {
  id: "governance",
  name: "Governance",
  description:
    "Groups that decide things together — membership with sponsorship, proposals and votes, work with uploaded proof paid in tokens, treasury, and a permanent activity log",
  version: "0.8.0",
  enabled: true,
  category: "business",
  order: 13,
  icon: "🗳️",
  // eslint-disable-next-line no-restricted-syntax -- module tile accent; matches the other moduleConfigs, which all carry a literal here
  color: "#0EA5E9",

  routes: [
    // Written education and fictional local-only rehearsal expose no tenant records.
    { path: "/governance/learn", component: GovLearn, public: true, useLayout: true },
    { path: "/governance/lab", component: GovLab, public: true, useLayout: true },
    { path: "/governance/guide", component: GovGuide, public: true, useLayout: true },
    { path: "/governance", component: GovOrganisations, permissionLevel: "view", useLayout: true },
    { path: "/governance/marketplace", component: GovMarketplace, permissionLevel: "view", useLayout: true, layoutOptions: { maxW: "container.xl", py: 6 } },
    // The literal segments precede `:id` so the param cannot swallow them.
    //
    // AUTH-ONLY routes (no `permissionLevel`, no `public`): the login modal still
    // applies, but a signed-in person WITHOUT the governance permission is not shown
    // "Access Denied" — the page asks `myGovAccess` and renders the front door
    // instead. Reads are the visitor-safe whitelist; every write is still gated
    // on membership and permission server-side. (Governance v0.2, 2026-09-11.)
    { path: "/governance/:orgId/proposals/:id", component: GovProposalPage, useLayout: true, layoutOptions: { maxW: "container.xl", py: 4 } },
    { path: "/governance/:id/join", component: GovJoin, useLayout: true, layoutOptions: { maxW: "container.xl", py: 6 } },
    { path: "/governance/:id/participation", component: GovMyParticipation, useLayout: true, layoutOptions: { maxW: "container.xl", py: 6 } },
    // One event, on its own page — where every notification this module sends now lands (2026-09-15).
    { path: "/governance/:orgId/activity/:id", component: GovActivityEntryPage, permissionLevel: "view", useLayout: true, layoutOptions: { maxW: "container.lg", py: 6 } },
    { path: "/governance/:orgId/work/:id", component: GovWorkPage, permissionLevel: "view", useLayout: true, layoutOptions: { maxW: "container.xl", py: 4 } },
    { path: "/governance/:id/notifications", component: GovNotifications, permissionLevel: "edit", useLayout: true, layoutOptions: { maxW: "container.lg", py: 6 } },
    { path: "/governance/:id/invites", component: GovInvites, permissionLevel: "create", useLayout: true, layoutOptions: { maxW: "container.xl", py: 6 } },
    { path: "/governance/:id/jobs", component: GovJobSpecs, permissionLevel: "view", useLayout: true, layoutOptions: { maxW: "container.xl", py: 6 } },
    { path: "/governance/:id/treasury", component: GovTreasury, permissionLevel: "view", useLayout: true, layoutOptions: { maxW: "container.xl", py: 6 } },
    { path: "/governance/:id/settings", component: GovOrgSettings, permissionLevel: "edit", useLayout: true, layoutOptions: { maxW: "container.lg", py: 6 } },
    // Public invitation link — no login. The slug IS the credential, so this route
    // is deliberately outside the layout and outside the permission gate.
    { path: "/governance/invite/:slug", component: GovInvitePublic, permissions: [], public: true },
    // The group's own public record. No login, and NOT under /governance — the
    // whole point is an address a group can print on a flyer. The backend
    // resolves the tenant off the organisation row, never off the context.
    { path: "/org/:slug", component: GovOrgPublic, permissions: [], public: true },

    { path: "/governance/:id", component: GovOrganisationHub, useLayout: true, layoutOptions: { maxW: "container.xl", py: 4 } },
  ],

  navigation: [
    { label: "How it works", path: "/governance/learn", icon: "📖", permissionLevel: "view" },
    { label: "Local test workspace", path: "/governance/lab", icon: "🧪", permissionLevel: "view" },
    { label: "Printable guide", path: "/governance/guide", icon: "☑️", permissionLevel: "view" },
    { label: "Organisations", path: "/governance", icon: "🗳️", permissionLevel: "view" },
    { label: "Job marketplace", path: "/governance/marketplace", icon: "🧰", permissionLevel: "view" },
  ],

  // Resolved from the backend SSOT at runtime — ships EMPTY on purpose.
  permissions: { view: [], create: [], edit: [], delete: [] },

  quickActions: [
    { label: "Governance", path: "/governance", icon: "🗳️", description: "Organisations, votes and work" },
  ],

  changelog: [
    {
      version: "0.8.0", releaseDate: "2026-09-18", title: "Rehearse human-led work, quality and test credit locally",
      features: [
        "A complete process explainer, local test workspace and A4 walkthrough",
        "Fictional human circle, company mandate and voluntary membership examples",
        "Server-checked test reservations, independent quality ballots and traceable TEST issuance",
        "Bounded monthly instances, correction and no-quorum paths, transfer and fictional redemption",
        "Existing grant recording now fixes the agreed amount and links accepted evidence atomically; it does not execute payment",
      ],
    },
    {
      version: "0.7.0",
      releaseDate: "2026-09-16",
      title: "Forward a motion to someone who isn't a member yet",
      features: [
        "✅ A forwarded motion link can show the whole motion — the case, the ballot options and where the vote stands — instead of a sign-in box that explains nothing",
        "✅ Under it: Join this group to vote, which signs them in on the same page and lands them on the join page",
        "✅ Three settings per group — nothing, a preview, or the whole motion — and every group keeps exactly what it had",
        "✅ Who voted and why, and the papers attached to a motion, are never published at any setting",
      ],
    },
    {
      version: "0.6.0",
      releaseDate: "2026-09-15",
      title: "Tag people on the record, and one page per event",
      features: [
        "✅ Type @ in a note, a reply or when you drop a document — pick a member, a staff member or a client with the arrow keys and tag them",
        "✅ Choose how each tagged person hears: email, a text, or both — and see afterwards who was actually told, and who could not be reached",
        "✅ Every event now has its own page, with the documents on it, who was tagged, and a way back to the activity log",
        "✅ Every link this module sends opens that event's page and signs the reader in; links out of the log open in a new tab",
        "✅ Drop a document and say what it is in the same step, instead of dropping first and explaining later",
      ],
    },
    {
      version: "0.5.0",
      releaseDate: "2026-09-14",
      title: "A change log for the rules",
      features: [
        "✅ Every change to the group's name, purpose, rules, token symbol or voting numbers is kept as a version — who changed it, when, and why",
        "✅ Compare any two versions and see exactly which lines moved, with changed numbers written out in words",
        "✅ Saving a rules change shows what you are about to change first, and asks for a note once the group is live",
        "✅ The public page says which version of the rules a visitor is reading, and when it last changed",
        "✅ The change log sits right under the rules, with the latest change visible before you edit",
        "✅ The rules are written in markdown, like a policy: Visual, Markdown and Preview tabs over the same text",
      ],
    },
    {
      version: "0.4.0",
      releaseDate: "2026-09-14",
      title: "A complete record: who did it, comments on files, and checklists",
      features: [
        "✅ Every act now reaches the group's log — invitations withdrawn or turned down, standard jobs written and revised, message rules set up or removed, treasury accounts added and balances restated, work cancelled",
        "✅ Every entry says WHO did it. Twenty-eight kinds of entry used to record the act and not the person",
        "✅ A photo or document carries its own conversation, with the number of comments shown on the file",
        "✅ Checklists: make your own or copy one the group shares, tick sub-tasks, and every tick is on the record with your name on it",
        "✅ A motion and a piece of work each show their own history",
        "✅ The log shows twenty entries at a time and says how many there are in total",
      ],
    },
    {
      version: "0.3.1",
      releaseDate: "2026-09-13",
      title: "Put a motion again, and a close you can't press by accident",
      features: [
        "✅ A finished motion can be put again: a new draft with the same words and choices, linked to the original both ways. Nothing is reopened and no record is edited",
        "✅ Closing now says what it is about to record and how many votes are in. With nobody having voted it offers Withdraw instead",
        "✅ Withdrawing asks why in the page's own dialog instead of a browser box, and the reason goes on the record",
      ],
    },
    {
      version: "0.3.0",
      releaseDate: "2026-09-12",
      title: "A calmer, clearer look — easy to read at any age",
      features: [
        "✅ Every page redesigned on one shared kit: glass cards, a clear page header, plain-word status badges and gentle motion that respects 'reduce motion'",
        "✅ Easy to read: 16px body text, nothing smaller than 14px, buttons at least 44px tall with words on them, and one main action per screen",
        "✅ A new group shows a six-step setup checklist with a progress ring, computed from the group's own record, and celebrates once when it is done",
        "✅ Voting shows a turnout ring with the quorum marked, a live countdown, big tap-friendly ballot choices, and confetti only after the server confirms your vote",
        "✅ Work shows a Claim → Prove → Accepted → Paid progress line from the item's real status",
        "✅ When the server can't be reached, pages say so in plain words with a Try again button, instead of 'Failed to fetch'",
        "✅ Every page checked at phone width (390px) with no sideways scrolling",
      ],
    },
    {
      version: "0.2.2",
      releaseDate: "2026-09-12",
      title: "Real member names, and deleting a group",
      features: [
        "✅ Members show the name on their client record, read live — fixes members listed as 'A member of this site', and names follow a rename",
        "✅ Applying from the Join page no longer sends a name; the server reads it from the applicant's own record",
        "✅ Delete a group from its Settings page after typing its name — allowed only while it holds no motions, work, tokens, treasury or job specs (archive those instead, which keeps the record)",
      ],
    },
    {
      version: "0.2.1",
      releaseDate: "2026-09-11",
      title: "Invite people already on your client list",
      features: [
        "✅ The invite dialog offers 'Someone on your client list' — search by name or email, filter by tag, tick several; each gets their own invitation from their client record",
        "✅ Shown only to people who can already search the client list; everyone else just types an email",
        "✅ Per-person results: who was emailed, who was saved, who failed and why — failures stay ticked for a second try",
      ],
    },
    {
      version: "0.2.0",
      releaseDate: "2026-09-11",
      title: "Seamless links, levels of participation, and a report that comes to you",
      features: [
        "✅ Any governance link can be forwarded — a stranger sees a written teaser and signs in on the same page (link previews are per organisation, off until switched on)",
        "✅ A front door at /governance/<id>/join for anyone signed in: purpose, rules, who's already here, and a choice of how involved to be",
        "✅ Levels of participation — Follow, Delegate, Vote (Sign shown for when a Safe is linked). Delegating freezes with the roll; a delegator can always override",
        "✅ My participation: a table of exactly which messages arrive, by email or text, plus a weekly or fortnightly written report",
        "✅ Notification rules gain SMS and a Followers audience; In-app is retired from the form (it never delivered) and a rule that sends nothing says so",
        "✅ Motions are written in a markdown editor and rendered everywhere a field was already named markdown",
        "✅ The activity log takes notes and files dropped anywhere on the hub, pages honestly, and any entry can become a draft motion that cites it",
        "✅ Tooltips no longer stick open after a click, module-wide",
        "ℹ️ Existing motion bodies now render as markdown — text written with * or # will format",
      ],
      notes:
        "Off-chain dry run throughout. The weekly report needs the governance digest job switched on (GOV_DIGEST_ENABLED) — until then the notifications page's 'Send any due reports now' is the only sender.",
    },
    {
      version: "0.1.0",
      releaseDate: "2026-09-04",
      title: "Initial release — off-chain governance that actually decides things",
      features: [
        "✅ Many organisations per tenant — registering one redeploys nothing",
        "✅ Membership with SPONSORSHIP: existing members vouch, and one member can't clear the bar alone",
        "✅ Proposals that FREEZE their rules and their electoral roll when they open",
        "✅ One member one vote, changeable until close; abstentions count for quorum, not against the motion",
        "✅ A tally computed by ONE function — what the page shows and what gets recorded cannot disagree",
        "✅ Work → claim → proof (IPFS CIDs) → review → tokens, every step a conditional write so nothing pays twice",
        "✅ Treasury ledger that always says where a balance came from and as at when",
        "✅ Append-only activity log — the record that makes 'who actually did what' checkable",
        "✅ Snapshot / Safe fields present and null: on-chain is phase two, and its absence is normal",
        "✅ Notification rules — opt-in, per audience, with 'never fired' visibly different from 'fired and failed'",
        "✅ Drag-and-drop proof upload through the platform's chunked path — nobody types a CID",
        "✅ Invitations with a private link, and a referral graph of who vouched for whom",
        "✅ Files dropped straight onto the record, with image/video/PDF previews and a full-screen viewer",
        "✅ Replies on any activity entry — additive, so the log stays append-only",
        "✅ Standardised job specifications: agreed once, priced once, run many times",
        "✅ A job marketplace across the tenant's organisations",
        "✅ Talk-it-through proposal drafting on your own Mac Studio",
        "✅ A treasury you can actually keep — accounts, restatements and a movement ledger",
        "✅ Editing a motion mid-vote is allowed but never quiet: a note is required and the ballot shows it",
        "✅ Settings, so an organisation can leave DRAFT and change its own rules",
        "✅ Nine data exporters, so a group can walk away with its whole record",
        "✅ A public /org/<slug> page — the group's decisions on the open internet, with the member roster and the referral graph deliberately held back",
        "✅ Closing-soon vote reminders that actually fire, swept from the open page rather than a cron that can silently stop",
      ],
      notes:
        "Off-chain by design so a group can hold a real vote in week one with zero gas and zero contracts. A proposal can name the State of Play episode it came out of — the only thing the two modules share.",
    },
  ],
};

export default governanceModuleConfig;
